import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Heart, X } from "lucide-react";
import { getToken } from "../api";
import { localApi } from "../config";
import { useAuth } from "../auth";

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;

// Modal to pay for a locked paid series from the viewer's wallet.
export const PaywallModal = ({ series, access, onClose, onOwned }) => {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [balance, setBalance] = useState(null);

  useState(() => {
    if (user) localApi("GET", "/wallet", undefined, getToken()).then((w) => setBalance(w.balance)).catch(() => {});
  });

  const insufficient = balance !== null && balance < access.price - 1e-9;

  const buy = async () => {
    setBusy(true);
    setErr("");
    try {
      await localApi("POST", `/series/${series.id}/buy`, {}, getToken());
      onOwned && onOwned();
    } catch (e) {
      if (e.status === 402 || /insufficient/i.test(e.message)) {
        localApi("GET", "/wallet", undefined, getToken()).then((w) => setBalance(w.balance)).catch(() => {});
        setErr("insufficient");
      } else {
        setErr(e.message || "Purchase failed — no money was taken.");
      }
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="paywall-modal">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0c0c12] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-crimson/15 text-crimson"><Lock size={20} /></span>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="paywall-close"><X size={18} /></button>
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-white">Unlock “{series.title}”</h3>
        <p className="mt-1 text-sm text-slate-400">One payment unlocks the whole series — every season and episode — forever, on web and in the app.</p>
        <p className="mt-5 font-heading text-3xl font-black text-white" data-testid="paywall-price">{gbp(access.price)}</p>

        {!user ? (
          <button onClick={() => navigate("/app/login", { state: { from: `/app/series/${series.id}` } })} data-testid="paywall-signin" className="mt-5 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white hover:bg-crimson-hover">
            Sign in to buy
          </button>
        ) : err === "insufficient" || insufficient ? (
          <div className="mt-5">
            <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-400" data-testid="paywall-insufficient">
              Your balance is {balance !== null ? gbp(balance) : "…"} and this series costs {gbp(access.price)}.
            </p>
            <button onClick={() => navigate("/app/wallet")} data-testid="paywall-deposit" className="mt-3 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white hover:bg-crimson-hover">
              Deposit
            </button>
          </div>
        ) : (
          <>
            {err && <p className="mt-4 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 text-sm text-crimson" data-testid="paywall-error">{err}</p>}
            <button onClick={buy} disabled={busy} data-testid="paywall-pay" className="mt-5 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
              {busy ? "Processing…" : `Pay ${gbp(access.price)} from wallet`}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// "Support" button + tip modal for videos, shorts and series episodes.
export const SupportButton = ({ target, creator, compact }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!creator || (user && creator.id === user.id)) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`support-${target.id}`} className={compact ? "flex flex-col items-center gap-1 text-white" : "inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-200 transition-colors hover:border-crimson/50"}>
        <Heart size={compact ? 22 : 14} className="text-crimson" />
        {compact ? <span className="font-mono text-[10px]">Support</span> : "Support"}
      </button>
      {open && <TipModal target={target} creator={creator} onClose={() => setOpen(false)} />}
    </>
  );
};

const QUICK = [1, 2, 5, 10];

const TipModal = ({ target, creator, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [balance, setBalance] = useState(null);

  useState(() => {
    if (user) localApi("GET", "/wallet", undefined, getToken()).then((w) => setBalance(w.balance)).catch(() => {});
  });

  const val = parseFloat(amount) || 0;
  const insufficient = balance !== null && val > 0 && balance < val - 1e-9;

  const send = async () => {
    if (!val || val <= 0) { setErr("Enter an amount."); return; }
    setBusy(true);
    setErr("");
    try {
      await localApi("POST", "/tips", { amount: val, target_id: target.id, target_title: target.title, creator_id: creator.id }, getToken());
      setDone(true);
    } catch (e) {
      if (/insufficient/i.test(e.message)) {
        localApi("GET", "/wallet", undefined, getToken()).then((w) => setBalance(w.balance)).catch(() => {});
        setErr("insufficient");
      } else setErr(e.message || "Tip failed — no money was taken.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="tip-modal">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0c0c12] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-crimson/15 text-crimson"><Heart size={20} /></span>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="tip-close"><X size={18} /></button>
        </div>
        {done ? (
          <div className="py-6 text-center">
            <p className="font-heading text-lg font-bold text-emerald-400" data-testid="tip-sent">Tip sent</p>
            <p className="mt-1 text-sm text-slate-400">Thank you for supporting {creator.displayName || creator.username}.</p>
            <button onClick={onClose} className="mt-5 rounded-full border border-white/15 px-6 py-2.5 font-heading text-xs font-bold text-slate-200 hover:border-white/40" data-testid="tip-done-close">Close</button>
          </div>
        ) : (
          <>
            <h3 className="mt-4 font-heading text-lg font-bold text-white">Support {creator.displayName || creator.username}</h3>
            <p className="mt-1 text-sm text-slate-400">Send a tip from your wallet.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {QUICK.map((a) => (
                <button key={a} onClick={() => setAmount(String(a))} data-testid={`tip-quick-${a}`} className={`rounded-full border px-4 py-2 font-heading text-sm font-bold transition-colors ${val === a ? "border-crimson bg-crimson/15 text-white" : "border-white/15 text-slate-300 hover:border-crimson/50"}`}>
                  £{a}
                </button>
              ))}
            </div>
            <div className="relative mt-3">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <input type="number" min="0.5" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Custom amount" data-testid="tip-amount" className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-8 pr-4 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60" />
            </div>
            {err === "insufficient" || insufficient ? (
              <div className="mt-4">
                <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-400" data-testid="tip-insufficient">
                  Your balance is {balance !== null ? gbp(balance) : "…"}{val > 0 ? ` and this tip is ${gbp(val)}` : ""}.
                </p>
                <button onClick={() => navigate("/app/wallet")} data-testid="tip-deposit" className="mt-3 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white hover:bg-crimson-hover">Deposit</button>
              </div>
            ) : (
              <>
                {err && <p className="mt-3 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 text-sm text-crimson" data-testid="tip-error">{err}</p>}
                <button onClick={send} disabled={busy || !val} data-testid="tip-send" className="mt-4 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
                  {busy ? "Sending…" : `Send ${val > 0 ? gbp(val) : "tip"}`}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
